<template>
  <div :class="objective.isImpulse ? 'border-outline-variant border' : 'bg-surface-container-high'">
    <div class="mb-1 flex flex-row items-center gap-1 px-4 pt-2">
      <div class="flex grow items-center gap-2">
        <BaseIcon> track_changes </BaseIcon>
        <span class="text-label-large">
          <span>{{ $t('objective.impulse.title') }}</span>
        </span>
      </div>
      <div class="-mr-2 flex shrink-0 flex-row gap-1 self-start">
        <BaseMenu v-if="menuItems.length > 0" :items="menuItems" v-slot="{ open }">
          <BaseIconButton size="XS" icon="more_vert" :force-pressed="open" />
        </BaseMenu>
      </div>
    </div>

    <div v-if="isEditing" class="px-4 pb-4">
      <BaseInputField
        type="text"
        autofocus
        v-model="editableObjective.title"
        required
        :label="$t('objective.impulse.title')"
        :class="objective.isImpulse ? 'bg-surface' : 'bg-surface-container-high'"
      />
      <div class="mt-2 flex w-full flex-row justify-end gap-1">
        <BaseTextButton @click="cancelEdit" size="XS">{{ $t('common.cancel') }}</BaseTextButton>
        <BaseTextButton @click="saveObjective" size="XS" color="filled">{{
          $t('common.save')
        }}</BaseTextButton>
      </div>
    </div>

    <div v-if="!isEditing" class="px-4 pb-4">
      <h4
        class="text-body-medium"
        :class="[
          controls.showEdit &&
            'hover:bg-surface-container rounded-small cursor-pointer hover:-m-1 hover:p-1',
        ]"
        @click="controls.showEdit ? editObjective() : undefined"
      >
        {{ objective.title }}
      </h4>
    </div>

    <BaseDialog
      type="basic"
      :title="$t('objective.delete.confirmation')"
      :description="$t('objective.delete.description')"
      :is-open="isOpenDeleteObjectiveDialog"
      @close="closeDeleteObjectiveDialog"
    >
      <template #dismiss>
        <BaseTextButton color="text" @click="closeDeleteObjectiveDialog">{{
          $t('common.cancel')
        }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton color="text" @click="deleteObjective">{{
          $t('objective.delete.action')
        }}</BaseTextButton>
      </template>
    </BaseDialog>
  </div>
</template>
<script setup lang="ts">
  import { computed, onBeforeUnmount, toRef, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useObjective } from '../../composables/use-objective'
  import { getObjectiveControls } from '../../composables/objective-controls'
  import BaseIconButton from '../base/button/icon-button/BaseIconButton.vue'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'
  import BaseMenu from '../base/menu/BaseMenu.vue'
  import type { MenuItemGroup } from '../base/menu/MenuItem'
  import BaseDialog from '../base/dialog/BaseDialog.vue'
  import BaseIcon from '../base/icon/BaseIcon.vue'
  import BaseInputField from '../base/form-fields/BaseInputField.vue'
  import type { Objective } from '../../composables/use-objectives'
  import { useUserStore } from '@/stores/user.store'
  import type { DialogPhase } from '@/core/models/dialog/dialog'
  import { useUnsavedChangesStore } from '@/stores/unsaved-changes.store'

  interface Props {
    objective: Objective
    isReadOnly?: boolean
    dialogPhase?: DialogPhase
  }

  const props = withDefaults(defineProps<Props>(), {
    isReadOnly: false,
  })

  const { t } = useI18n()
  const { user } = useUserStore()

  const {
    isEditing,
    editableObjective,
    cancelEdit,
    saveObjective,
    editObjective,
    deleteObjective,
    openDeleteObjectiveDialog,
    closeDeleteObjectiveDialog,
    isOpenDeleteObjectiveDialog,
  } = useObjective(toRef(props, 'objective'))

  const { setSourceDirty } = useUnsavedChangesStore()

  watch(
    isEditing,
    (editing) => {
      setSourceDirty(props.objective.id, editing)
    },
    { immediate: true },
  )

  onBeforeUnmount(() => {
    setSourceDirty(props.objective.id, false)
  })

  const controls = computed(() =>
    getObjectiveControls(props.objective, {
      isReadOnly: props.isReadOnly,
      currentUserId: user.id,
      dialogPhase: props.dialogPhase,
    }),
  )

  const menuItems = computed(() => {
    const items: MenuItemGroup[] = []
    if (controls.value.showEdit) {
      items.push({
        items: [
          {
            id: 'edit',
            leadingIcon: 'edit',
            title: t('common.edit'),
            callback: () => {
              editObjective()
            },
          },
        ],
      })
    }
    if (controls.value.showDelete) {
      items.push({
        items: [
          {
            id: 'delete',
            leadingIcon: 'delete',
            destructive: true,
            title: t('common.delete'),
            callback: () => {
              openDeleteObjectiveDialog()
            },
          },
        ],
      })
    }

    return items
  })
</script>
